<template>
  <SettingsPaneV2 header="Workspace settings">
    <template #subheader>Change the settings for your current workspace here</template>
    <template #body>
      <div class="team__body__section">
        <div class="subheader__title">Details</div>
        <div class="subheader__text">General settings regarding your workspace</div>
      </div>

      <div class="team__body__section">
        <AvatarUpload
          :src="avatarUrl"
          :name="teamDisplayName"
          :company-url="companyUrl"
          :can-upload="isAuthorized('update_team')"
          :disabled="loading"
          :loading="!!avatarData && loading"
          upload-item-text="Upload workspace logo"
          reset-item-text="Remove workspace logo"
          @change="onAvatarChange"
        />
        <InputField
          ref="teamNameField"
          v-model="teamName"
          :error="error.teamName"
          :disabled="!isAuthorized('update_team')"
          name="teamName"
          label="Workspace name"
        />
        <div class="team__pair">
          <div class="team__key">
            Workspace created on
            <span class="team__value">
              {{ createdAt }}
            </span>
          </div>
        </div>
      </div>

      <Divider class="divider" />

      <div
        v-if="isAuthorized('update_team')"
        class="team__body__section"
      >
        <div class="control__toggle">
          <Toggle
            v-model="annotatorsCanCreateTagsAndAttributes"
            kind="primary"
            size="small"
          >
            <div>
              <p class="control__toggle-label">Allow workers to create new tags and attributes</p>
              <p class="control__toggle-sub-label">
                Allow annotators to type in any new tag or attribute.
              </p>
            </div>
          </Toggle>
        </div>
      </div>

      <div
        v-if="isAuthorized('update_team')"
        class="team__body__section"
      >
        <div class="control__toggle">
          <Toggle
            v-model="restrictExports"
            kind="primary"
            size="small"
          >
            <div>
              <p class="control__toggle-label">Restrict datasets exports</p>
              <p class="control__toggle-sub-label">
                Restrict dataset export to owners and admins only.
              </p>
            </div>
          </Toggle>
        </div>
      </div>

      <Divider class="divider" />

      <div
        v-if="isAuthorized('archive_team')"
        class="team__delete"
      >
        <div class="team__delete--text">
          <div class="subheader__title">Delete this workspace</div>
          <div class="subheader__text">
            This will delete all datasets and remove all members from the workspace
          </div>
        </div>
        <DarwinButton
          size="sm"
          kind="negative"
          flair="rounded"
          @click="onDeleteOrLeave"
        >
          Delete workspace {{ teamDisplayName }}
        </DarwinButton>
      </div>

      <div
        v-else
        class="team__delete"
      >
        <div class="team__delete--text">
          <div class="subheader__title">Leave this workspace</div>
          <div class="subheader__text">
            This will unassign any tasks or datasets associated to your account and you will no
            longer have access to this workspace
          </div>
        </div>
        <DarwinButton
          size="sm"
          kind="negative"
          flair="rounded"
          @click="onDeleteOrLeave"
        >
          Leave workspace {{ teamDisplayName }}
        </DarwinButton>
      </div>
    </template>

    <template #footer>
      <DarwinButton
        v-if="isAuthorized('update_team')"
        size="md"
        kind="primary"
        flair="rounded"
        :disabled="loading"
        @click="submit"
      >
        Save
      </DarwinButton>
    </template>

    <DarwinSafeDialog
      :open="deleteTeamDialoOpen"
      :target-text="deleteDialogButtonTarget"
      :min-height="260"
      :max-width="600"
      kind="negative"
      @cancel="deleteTeamDialoOpen = false"
      @confirm="onDeleteDialogConfirmed"
    >
      <template #title>{{ deleteDialogTitle }}</template>
      <template #detail>
        {{ deleteDialogButtonTarget }}
      </template>
      <template #body>
        {{ deleteDialogDetail }}
      </template>
      <template #confirm-text>{{ deleteDialogButtonText }}</template>
    </DarwinSafeDialog>
  </SettingsPaneV2>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, ref, watch } from 'vue'
import AvatarUpload from '@/components/Common/AvatarUpload/AvatarUpload.vue'
import type { AvatarUploadData } from '@/components/Common/AvatarUpload/types'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import { getDomainFromEmail } from '@/core/utils/string'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useAuth } from '@/modules/Auth/useAuth'
import { useToast } from '@/uiKit/Toast/useToast'
import { formatDate } from '@/core/utils/time'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import Toggle from '@/uiKit/Toggle/Toggle.vue'
import SettingsPaneV2 from './SettingsPaneV2.vue'
import Divider from '@/uiKit/Divider/Divider.vue'
import { useUserStore } from '@/modules/Auth/useUserStore'
import DarwinSafeDialog from '@/uiKit/Dialog/DarwinSafeDialog.vue'
import type { UpdateTeamPayload } from '@/modules/Teams/types'
import { createUpdateTeamError } from '@/modules/Teams/types'

const { isAuthorized } = useAuth()
const authStore = useAuthStore()
const teamStore = useTeamStore()
const toast = useToast()
const userStore = useUserStore()

const deleteTeamDialoOpen = ref(false)
const teamNameField = ref<InstanceType<typeof InputField>>()

const memberships = computed(() => teamStore.allRelevantMemberships)

const avatarUrl = computed(
  () => teamStore.currentTeam?.image?.thumbnail_url || teamStore.currentTeam?.image?.url || null,
)

const companyUrl = computed(() => {
  const owner = memberships.value.find((m) => m.role === 'owner')

  if (!owner) {
    return null
  }

  return getDomainFromEmail(owner.email)
})

const teamDisplayName = computed(() => teamStore.currentTeam?.name || '')

const createdAt = computed(() =>
  teamStore.currentTeam?.inserted_at
    ? formatDate(teamStore.currentTeam.inserted_at, 'MMM D, YYYY')
    : '',
)

const deleteDialogTitle = computed(() =>
  isAuthorized('archive_team')
    ? 'Please confirm you wish to delete this workspace'
    : 'Please confirm you wish to leave this workspace',
)

const deleteDialogButtonText = computed(
  () => `${isAuthorized('archive_team') ? 'Delete' : 'Leave'}`,
)

const deleteDialogButtonTarget = computed(
  () => `${isAuthorized('archive_team') ? 'DELETE' : 'LEAVE'} ${teamDisplayName.value}`,
)

const deleteDialogDetail = computed(() =>
  isAuthorized('archive_team')
    ? `Please note that deleting a workspace is irreversible.
       To confirm that you wish to proceed deleting this workspace, please type the following:
       '${deleteDialogButtonTarget.value}'`
    : `Leaving a workspace can't be undone later on.
       To confirm that you wish to proceed leaving this workspace, please type the following:
       '${deleteDialogButtonTarget.value}'`,
)
const teamName = ref('')
const restrictExports = ref(false)
const annotatorsCanCreateTagsAndAttributes = ref(false)

const updateData = (): void => {
  if (!teamStore.currentTeam) {
    return
  }

  teamName.value = teamStore.currentTeam.name
  restrictExports.value = teamStore.currentTeam.restrict_exports
  annotatorsCanCreateTagsAndAttributes.value =
    teamStore.currentTeam.annotators_can_create_tags_and_attributes
}

watch(
  () => teamStore.currentTeam,
  () => {
    updateData()
  },
)

onBeforeMount(() => {
  updateData()
})

const avatarData = ref<AvatarUploadData | null>(null)
const onAvatarChange = (event: AvatarUploadData | null): void => {
  avatarData.value = event
}

const error = ref<{ teamName?: string }>({})

const validateForm = (): boolean => {
  error.value = {}

  return Object.keys(error.value).length === 0
}

const loading = ref(false)

const submit = async (): Promise<void> => {
  if (!validateForm()) {
    return
  }

  if (!teamStore.currentTeam) {
    return
  }

  loading.value = true
  const params: UpdateTeamPayload = {
    name: teamName.value,
    annotators_can_create_tags_and_attributes: annotatorsCanCreateTagsAndAttributes.value,
    restrict_exports: restrictExports.value,
  }

  if (avatarData.value) {
    // We need to send the avatar hash to update the team, and receive the upload_url for the avatar
    params.hash = avatarData.value.hash
  }

  const response = await teamStore.updateTeam(teamStore.currentTeam.id, params)

  if (!response.ok) {
    const updateError = createUpdateTeamError(response.error)
    error.value = {}
    error.value.teamName = updateError.name
    loading.value = false
    return
  }

  if (avatarData.value) {
    await teamStore.updateTeamAvatar(
      response.data,
      avatarData.value.file,
      avatarData.value.type || '',
    )
  }

  loading.value = false
  avatarData.value = null

  toast.success({
    meta: { title: 'Workspace Profile successfully updated' },
  })
}

const onDeleteOrLeave = (): void => {
  deleteTeamDialoOpen.value = true
}

const onDeleteConfirmed = async (): Promise<void> => {
  if (!teamStore.currentTeam) {
    return
  }

  loading.value = true

  const response = await teamStore.deleteTeam(teamStore.currentTeam.id)

  loading.value = false

  if (!response.ok) {
    toast.error({ meta: { title: response.error.message.toString() } })
    return
  }

  await authStore.logout()
}

const onLeaveConfirmed = async (): Promise<void> => {
  const userId = userStore.currentUserId
  if (!userId) {
    return
  }

  const currentMembership = memberships.value.find((m) => m.user_id === userId)

  if (!currentMembership) {
    return
  }

  loading.value = true

  const response = await teamStore.leaveTeam(currentMembership.id)

  loading.value = false

  if (!response.ok) {
    toast.error({ meta: { title: response.error.message.toString() } })
    return
  }

  await authStore.logout()
}

const onDeleteDialogConfirmed = (): void => {
  if (isAuthorized('archive_team')) {
    onDeleteConfirmed()
    return
  }
  onLeaveConfirmed()
}
</script>

<style lang="scss" scoped>
.team__body__section {
  margin-bottom: 16px;
}

.subheader__title {
  @include typographyBoldBody200;
  color: var(--color-text-default);
  margin-bottom: 4px;
}

.subheader__text {
  @include typographyRegularBody200;
  color: var(--color-text-subtlest);
}

.divider {
  margin: 32px 0;
}
.control__toggle {
  display: flex;
  align-items: center;
}

.control__toggle-label,
.control__toggle-sub-label {
  @include typographyRegularBody200;
  color: var(--color-text-default);
  margin-left: 16px;
}
.control__toggle-sub-label {
  color: var(--color-text-subtlest);
}

.team__pair {
  margin: 15px 30px 0 0;
  @include typographyRegularBody200;
}

.team__key {
  color: var(--color-text-subtlest);
}

.team__value {
  color: var(--color-text-default);
}

.team__delete {
  margin-top: 30px;
  @include row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &--text {
    flex: 1;
  }
}
</style>
